import Controller from "controllers/application_controller"
import { PopoverPositioning } from "../lib/popover_positioning"

/**
 * Menu behaviour for a [popover] dropdown menu (see dropdown_menu.css for the
 * DOM contract). The Popover API already handles open/close and light dismiss;
 * this controller adds APG menu-button behaviour: arrow/Home/End navigation,
 * focus follows pointer, close on item activation or Tab.
 *
 * Escape is handled here rather than left to light dismiss, which closes the
 * menu without cancelling the keydown. Document-level Escape handlers would
 * then fire too and close whatever surrounds us — Ui::Modal's dialog,
 * Ui::Filter::List's panel. Cancelling the event is the contract Ui::Modal
 * honours; propagation is stopped as well for handlers that don't check. Same
 * arrangement as ui-select.
 */
export default class extends Controller {
  static identifier = "ui-dropdown-menu"

  connect() {
    this.element.tabIndex = -1
    this.trigger = document.querySelector(`[popovertarget="${this.element.id}"]`)
    this.trigger?.setAttribute("aria-haspopup", "menu")
    this.trigger?.setAttribute("aria-expanded", "false")

    this.positioning = new PopoverPositioning({
      reference: this.trigger,
      floating: this.element,
      namePrefix: "ui-dropdown-menu"
    })

    this.addEventListener("toggle", event => this.toggled(event))
    this.addEventListener("click", event => this.activated(event))
    this.addEventListener("keydown", event => this.keydown(event))
    this.addEventListener("pointerover", event => this.focusHoveredItem(event))
  }

  disconnect() {
    this.positioning?.stop()
    super.disconnect()
  }

  // aria-expanded mirrors Base UI's trigger, so .ui-button's aria-expanded
  // styles mark the trigger while its menu is open.
  toggled(event) {
    const open = event.newState === "open"
    this.trigger?.setAttribute("aria-expanded", String(open))

    if (open) {
      this.positioning.start()

      if (this.trigger?.matches(":focus-visible")) {
        this.items[0]?.focus()
      } else {
        this.element.focus()
      }
    } else {
      this.positioning.stop()
    }
  }

  activated(event) {
    const item = event.target.closest("[role='menuitem']")
    if (item && this.items.includes(item)) this.hide()
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.hide()
      // preventDefault cancels the popover's own focus restore along with its
      // light dismiss, so put focus back on the trigger ourselves.
      this.trigger?.focus()
      return
    }

    if (event.key === "Tab") {
      this.hide()
      return
    }

    const { items } = this
    const index = items.indexOf(document.activeElement)
    let next

    switch (event.key) {
      case "ArrowDown":
        next = items[index + 1] || items[0]
        break
      case "ArrowUp":
        next = items[index - 1] || items.at(-1)
        break
      case "Home":
        next = items[0]
        break
      case "End":
        next = items.at(-1)
        break
      default:
        return
    }

    event.preventDefault()
    next?.focus()
  }

  focusHoveredItem(event) {
    const item = event.target.closest("[role='menuitem']")
    if (item && item !== document.activeElement && this.items.includes(item)) item.focus()
  }

  get items() {
    return [...this.element.querySelectorAll("[role='menuitem']:not([disabled]):not([aria-disabled='true'])")]
  }

  hide() {
    // A click or Tab landing during the exit fade would throw InvalidStateError
    if (this.element.matches(":popover-open")) this.element.hidePopover()
  }
}
